import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CancelIcon from '@mui/icons-material/Cancel';
import Divider from "@mui/material/Divider";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Table from '../helpers/Table'
import DeletePopup from "../helpers/DeletePopup";

import { addDriver, editDriver, deleteDriver } from "../../actions/valueActions";

import { createChip } from "./valueFunctions";
import { formatString, convertBoolDiffValue } from "./valueFunctions";
import { getIconFromDriverName } from "./iconFunctions";
import RevenueValueDriverCLVForm from "./RevenueValueDriverCLVForm";
import { getAllOutcomes } from "./valueFunctions";

const RevenueValueDriverCLV = ({ id, valueModel, driver, outcome }) => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();

    const [editCLVDriverId, setEditCLVDriverId] = useState(null);
    const [deleteCLVDriverPopupOpen, setDeleteCLVDriverPopupOpen] = useState(false);
    const [deleteCLVDriverId, setDeleteCLVDriverId] = useState(null);

    const handleResetCLV = () => {
        setEditCLVDriverId(null)

        // Access the formikRef current value and call resetForm
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
    };

    const openDeleteCLVDriver = (id) => {
        setDeleteCLVDriverId(id)
        setDeleteCLVDriverPopupOpen(true)
    }

    const editCLVDriver = (id) => {
        setEditCLVDriverId(id)
    }

    const onSubmitCLV = async (values, driver, { setSubmitting, setErrors }) => {
        values.id = editCLVDriverId
        if (outcome) {
            values.outcome = outcome;
        }
        values.driver = driver
        values.annual_contract_value = values.annual_contract_value.toString().replace(/,/g, "");
        values.transaction_value = values.transaction_value.toString().replace(/,/g, "");
        let success = false

        if (editCLVDriverId) {
            const result = await dispatch(editDriver('clv', id, values, { setSubmitting, setErrors }))
            success = result?.success;
            if (success) {
                setEditCLVDriverId(null)
            }
        }
        else {
            const result = await dispatch(addDriver('clv', id, values, { setSubmitting, setErrors }))
            success = result?.success;
        }

        if (formikRef.current && success) {
            formikRef.current.resetForm();
        }
    }

    const clvDriverColumns = [
        { id: 'outcome', label: 'Outcome', minWidth: 170, action: false },
        { id: 'driver_name', label: 'Name', minWidth: 170, action: false },
        // { id: 'revenue_model', label: 'Revenue model', format: (value) => formatString(value), minWidth: 100, action: false },
        { id: 'clv_change', label: 'Change values', minWidth: 150, action: false },
        { id: 'positive_driver', label: 'Differentiation value', minWidth: 100, format: (value) => convertBoolDiffValue(Boolean(value)), action: false },
        { id: 'actions', label: 'Actions', minWidth: 100, action: false },
    ];

    const createCLVDriverData = (id, outcome, driver_name, revenue_model, clv_change, positive_driver, actions) => {
        return {
            id,
            outcome,
            driver_name,
            revenue_model,
            clv_change: createChip(clv_change, {
                'annual_contract_value': `% ${positive_driver ? 'increase' : 'decrease'}`,
                'transaction_value': `% ${positive_driver ? 'increase' : 'decrease'}`,
                'transaction_frequency': ` times ${positive_driver ? 'increase' : 'decrease'}`,
                'contract_length': `% ${positive_driver ? 'increase' : 'decrease'}`,
                'churn_rate': `% ${positive_driver ? 'decrease' : 'increase'}`,
            }),
            positive_driver,
            actions
        };
    }

    const clvDriverActions = (id) => {
        return (
            <React.Fragment>
                <IconButton size="small" onClick={() => editCLVDriver(id)}>
                    <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => openDeleteCLVDriver(id)}>
                    <DeleteIcon />
                </IconButton>
            </React.Fragment>
        )
    }

    const clvDriverRows = valueModel?.revenue_value_driver.lifetime_value.filter(driver => !outcome || driver.outcome === outcome).map((driver) => {
        return (
            createCLVDriverData(driver.id, driver.outcome, driver.driver_name, driver.revenue_model, driver.clv_change, driver.positive_driver, clvDriverActions(driver.id))
        )
    })

    const clvDriverTable = () => {
        return (
            <React.Fragment>
                {/* <Divider /> */}
                <Table
                    columns={clvDriverColumns}
                    rows={clvDriverRows}
                    selectedId={editCLVDriverId}
                />
            </React.Fragment>
        )
    }

    const clvDriverFormActions = () => {
        return (
            <React.Fragment>
                {editCLVDriverId ? null
                    : <Button variant="contained" startIcon={<Box component={getIconFromDriverName(driver)} />} size="small" type="submit">Add driver</Button>
                }
                {editCLVDriverId ?
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" startIcon={<EditIcon />} size="small" type="submit">Save</Button>
                        <Button variant="contained" startIcon={<CancelIcon />} size="small" onClick={handleResetCLV}>Cancel</Button>
                    </Stack>
                    : null
                }
            </React.Fragment>
        )
    }

    return (
        <Box>
            <Divider sx={{ mb: 3 }} />
            <Stack gap={1} direction="row" sx={{ ml: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    Driver type:
                </Typography>
                <Typography variant="caption">
                    Marketing and Sales
                </Typography>
            </Stack>
            <RevenueValueDriverCLVForm
                currency="€"
                outcome={outcome}
                outcomeList={getAllOutcomes(valueModel?.driver_analysis)}
                editCLVDriverId={editCLVDriverId}
                editCLVDriver={valueModel?.revenue_value_driver.lifetime_value.find(clvDriver => clvDriver.id === editCLVDriverId)}
                formActions={clvDriverFormActions}
                onSubmit={(values, { setSubmitting, setErrors }) => onSubmitCLV(values, driver, { setSubmitting, setErrors })}
                ref={formikRef}
            />
            {clvDriverTable()}
            <DeletePopup open={deleteCLVDriverPopupOpen} onClose={() => setDeleteCLVDriverPopupOpen(false)} onDelete={() => dispatch(deleteDriver('clv', id, deleteCLVDriverId))} />
        </Box>
    )

}

export default RevenueValueDriverCLV